"use client";

import { useEffect, useRef, useState } from "react";
import {
  CopyButton,
  PillGroup,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  CARD,
  HINT,
  LABEL,
  TEXTAREA_PROSE,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  CornellPage,
  PrintedNotes,
} from "@/components/tools/study/cornell-sheets";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { downloadText } from "@/lib/tool-kit/download";
import {
  type CornellHeader,
  type CornellRow,
  cornellGeometry,
  cornellText,
  PAPER,
  type PaperSize,
  parseSavedCornell,
  type Ruling,
} from "@/lib/tool-kit/study/cornell";
import { randomToken } from "@/lib/tool-kit/study/dates";

const STORAGE_KEY = "citationeasy-cornell-notes-v1";
const PAGE_OPTIONS = ["1", "2", "3", "4", "5", "6", "8", "10"] as const;
const RULINGS: readonly Ruling[] = ["wide", "college", "narrow", "blank"];

type Mode = "template" | "online";

/** English strings. A translated content file overrides these through `ui`. */
export const CORNELL_NOTES_TEMPLATE_STRINGS = {
  modeLabel: "Mode",
  modes: { template: "Printable template", online: "Take notes online" },
  nameLabel: "Name",
  classLabel: "Class",
  dateLabel: "Date",
  topicLabel: "Topic",
  headerHint:
    "Whatever you type in these fields appears in the header; leave one empty to write it in by hand.",
  paperLabel: "Paper size",
  papers: { letter: "US Letter", a4: "A4" },
  rulingLabel: "Lines",
  rulings: {
    wide: "Wide ruled (8.7 mm)",
    college: "College ruled (7.1 mm)",
    narrow: "Narrow ruled (6.4 mm)",
    blank: "No lines",
  },
  pagesLabel: "Pages to print",
  previewLabel: "Preview of one Cornell notes page",
  cueHeading: "Cues & questions",
  notesHeading: "Notes",
  summaryHeading: "Summary",
  pageCount: "one:{count} page|other:{count} pages",
  print: "Print {pages}",
  printHint:
    "Pick the same paper size in the print dialog and print at 100% scale with default margins.",
  cueLabel: "Cue or question",
  cuePlaceholder: "e.g. What goes into photosynthesis?",
  noteLabel: "Notes",
  notePlaceholder: "What the lecture or reading said, in your own words",
  summaryLabel: "Summary",
  summaryPlaceholder:
    "Two or three sentences, written from memory: the main ideas of these notes",
  rowLabel: "Row {number}",
  addRow: "Add a row",
  removeRow: "Remove row {number}",
  copyText: "Copy as text",
  copied: "Copied!",
  downloadTxt: "Download .txt",
  txtFileName: "cornell-notes.txt",
  printNotes: "Print notes",
  clearNotes: "Clear notes",
  confirmClear: "Click again to clear",
  cueTextLabel: "Cue",
  fieldLabel: "{label}:",
  storageNote:
    "The header fields and notes typed here are saved in this browser's local storage, so they're still here if you reload. Nothing is uploaded.",
  storageUnavailable:
    "This browser isn't letting the page save data, so your notes will clear when you close the page. Copy or download them first.",
} as const;

const EMPTY_HEADER: CornellHeader = {
  name: "",
  course: "",
  date: "",
  topic: "",
};
const FIRST_ROWS: CornellRow[] = [
  { id: "r1", cue: "", note: "" },
  { id: "r2", cue: "", note: "" },
  { id: "r3", cue: "", note: "" },
];

export default function CornellNotesTemplateWidget() {
  const strings = useToolUi(CORNELL_NOTES_TEMPLATE_STRINGS);
  const locale = useToolLocale();
  const [mode, setMode] = useState<Mode>("template");
  const [header, setHeader] = useState<CornellHeader>(EMPTY_HEADER);
  const [paper, setPaper] = useState<PaperSize>("letter");
  const [ruling, setRuling] = useState<Ruling>("college");
  const [pages, setPages] = useState<(typeof PAGE_OPTIONS)[number]>("1");
  const [rows, setRows] = useState<CornellRow[]>(FIRST_ROWS);
  const [summary, setSummary] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [canSave, setCanSave] = useState(true);
  const [clearArmed, setClearArmed] = useState(false);
  const rowCounter = useRef(0);

  useEffect(() => {
    let raw: string | null = null;
    try {
      raw = window.localStorage.getItem(STORAGE_KEY);
    } catch {
      setCanSave(false);
    }
    const saved = parseSavedCornell(raw);
    if (saved) {
      setHeader(saved.header);
      setRows(saved.rows.length > 0 ? saved.rows : FIRST_ROWS);
      setSummary(saved.summary);
    }
    setLoaded(true);
  }, []);

  useEffect(() => {
    if (!(loaded && canSave)) {
      return;
    }
    const timer = setTimeout(() => {
      try {
        window.localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({ header, rows, summary })
        );
      } catch {
        setCanSave(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [loaded, canSave, header, rows, summary]);

  const geometry = cornellGeometry(paper, ruling);
  const sheetLabels = {
    name: strings.nameLabel,
    course: strings.classLabel,
    date: strings.dateLabel,
    topic: strings.topicLabel,
    cues: strings.cueHeading,
    notes: strings.notesHeading,
    summary: strings.summaryHeading,
    preview: strings.previewLabel,
    field: strings.fieldLabel,
  };
  const pageTotal = Number(pages);
  const pageLabel = tPlural(strings.pageCount, locale, pageTotal, {
    count: pageTotal,
  });
  const notesText = cornellText(
    { ...header, rows, summary },
    {
      name: strings.nameLabel,
      course: strings.classLabel,
      date: strings.dateLabel,
      topic: strings.topicLabel,
      cues: strings.cueTextLabel,
      notes: strings.notesHeading,
      summary: strings.summaryHeading,
    }
  );

  const updateRow = (id: string, patch: Partial<CornellRow>) =>
    setRows((current) =>
      current.map((row) => (row.id === id ? { ...row, ...patch } : row))
    );

  function addRow() {
    rowCounter.current += 1;
    setRows((current) => [
      ...current,
      { id: `${randomToken(8)}-${rowCounter.current}`, cue: "", note: "" },
    ]);
  }

  function clearNotes() {
    if (!clearArmed) {
      setClearArmed(true);
      return;
    }
    setRows(FIRST_ROWS);
    setSummary("");
    setClearArmed(false);
  }

  return (
    <>
      <style>{`@media print { @page { size: ${PAPER[paper].css} portrait; margin: ${PAPER[paper].margin}mm; } }`}</style>
      <section className="space-y-4 print:hidden">
        <PillGroup
          label={strings.modeLabel}
          name="cn-mode"
          onChange={setMode}
          options={[
            { value: "template", label: strings.modes.template },
            { value: "online", label: strings.modes.online },
          ]}
          value={mode}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="cn-name"
            label={strings.nameLabel}
            onChange={(name) => setHeader({ ...header, name })}
            value={header.name}
          />
          <TextField
            id="cn-class"
            label={strings.classLabel}
            onChange={(course) => setHeader({ ...header, course })}
            value={header.course}
          />
          <TextField
            id="cn-date"
            label={strings.dateLabel}
            onChange={(date) => setHeader({ ...header, date })}
            value={header.date}
          />
          <TextField
            id="cn-topic"
            label={strings.topicLabel}
            onChange={(topic) => setHeader({ ...header, topic })}
            value={header.topic}
          />
        </div>
        <p className={HINT}>{strings.headerHint}</p>
      </section>

      {mode === "template" ? (
        <>
          <section className="space-y-4 print:hidden">
            <div className="grid gap-4 sm:grid-cols-3">
              <SelectField
                id="cn-paper"
                label={strings.paperLabel}
                onChange={setPaper}
                options={[
                  { value: "letter", label: strings.papers.letter },
                  { value: "a4", label: strings.papers.a4 },
                ]}
                value={paper}
              />
              <SelectField
                id="cn-ruling"
                label={strings.rulingLabel}
                onChange={setRuling}
                options={RULINGS.map((value) => ({
                  value,
                  label: strings.rulings[value],
                }))}
                value={ruling}
              />
              <SelectField
                id="cn-pages"
                label={strings.pagesLabel}
                onChange={setPages}
                options={PAGE_OPTIONS.map((value) => ({ value, label: value }))}
                value={pages}
              />
            </div>
            <div className="mx-auto max-w-md rounded-lg border border-border bg-white p-3 shadow-sm">
              <CornellPage
                geometry={geometry}
                header={header}
                labels={sheetLabels}
                print={false}
              />
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <button
                className={BUTTON_PRIMARY}
                onClick={() => window.print()}
                type="button"
              >
                {t(strings.print, { pages: pageLabel })}
              </button>
              <p className={HINT}>{strings.printHint}</p>
            </div>
          </section>
          <div aria-hidden="true" className="hidden print:block">
            {Array.from({ length: pageTotal }, (_, index) => index).map(
              (index) => (
                <CornellPage
                  geometry={geometry}
                  header={header}
                  key={index}
                  labels={sheetLabels}
                  print
                />
              )
            )}
          </div>
        </>
      ) : (
        <>
          <section className="space-y-4 print:hidden">
            <h2 className={WIDGET_H2}>
              {header.topic || strings.modes.online}
            </h2>
            <ol className="space-y-3">
              {rows.map((row, index) => (
                <li className={`${CARD} space-y-2`} key={row.id}>
                  <div className="flex items-center justify-between gap-3">
                    <span className={LABEL}>
                      {t(strings.rowLabel, { number: index + 1 })}
                    </span>
                    <button
                      aria-label={t(strings.removeRow, { number: index + 1 })}
                      className={BUTTON_SMALL}
                      disabled={rows.length === 1}
                      onClick={() =>
                        setRows(rows.filter((item) => item.id !== row.id))
                      }
                      type="button"
                    >
                      ×
                    </button>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-[2fr_5fr]">
                    <TextAreaField
                      id={`cn-cue-${row.id}`}
                      label={strings.cueLabel}
                      onChange={(cue) => updateRow(row.id, { cue })}
                      placeholder={strings.cuePlaceholder}
                      prose
                      rows={3}
                      value={row.cue}
                    />
                    <TextAreaField
                      id={`cn-note-${row.id}`}
                      label={strings.noteLabel}
                      onChange={(note) => updateRow(row.id, { note })}
                      placeholder={strings.notePlaceholder}
                      prose
                      rows={5}
                      value={row.note}
                    />
                  </div>
                </li>
              ))}
            </ol>
            <button className={BUTTON_SECONDARY} onClick={addRow} type="button">
              {strings.addRow}
            </button>
            <div className="space-y-2">
              <label className={LABEL} htmlFor="cn-summary">
                {strings.summaryLabel}
              </label>
              <textarea
                className={`${TEXTAREA_PROSE} min-h-[120px]`}
                id="cn-summary"
                onChange={(event) => setSummary(event.target.value)}
                placeholder={strings.summaryPlaceholder}
                value={summary}
              />
            </div>
            <div className="flex flex-wrap gap-3">
              <CopyButton
                copiedLabel={strings.copied}
                label={strings.copyText}
                text={notesText}
              />
              <button
                className={BUTTON_SECONDARY}
                disabled={notesText === ""}
                onClick={() => downloadText(strings.txtFileName, notesText)}
                type="button"
              >
                {strings.downloadTxt}
              </button>
              <button
                className={BUTTON_SECONDARY}
                onClick={() => window.print()}
                type="button"
              >
                {strings.printNotes}
              </button>
              <button
                className={BUTTON_SMALL}
                onClick={clearNotes}
                type="button"
              >
                {clearArmed ? strings.confirmClear : strings.clearNotes}
              </button>
            </div>
            <p className={HINT}>
              {canSave ? strings.storageNote : strings.storageUnavailable}
            </p>
          </section>
          <PrintedNotes
            header={header}
            labels={sheetLabels}
            rows={rows}
            summary={summary}
          />
        </>
      )}
    </>
  );
}
